import { inject, injectable } from 'inversify';
import { makeAutoObservable } from 'mobx';
import { type OsiPastInvoicesViewModel } from '@shared/types/mobx/widgets/OsiPastInvoicesWidget';
import { queryClient } from '@shared/api/reactQuery';
import { flatComparator } from '@shared/utils/helpers/flatComparator';
import { type GridSelectionModel, type GridInputSelectionModel, GridRowId } from '@mui/x-data-grid-pro';
import fileDownload from 'js-file-download';
import { notifications } from '@shared/utils/notifications';
import axios, { type AxiosError } from 'axios';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { SEPARATE_INVOICES_REQUEST_TIMEOUT } from '@shared/api/config';
import { downloadPdfOrZip } from '@shared/utils/download';
import { getPostApiReportsAbonentsInvoicesPdfOnPeriodMutationOptions } from '@/shared/api/orval/reports/reports';
import { getGetApiOsiIdAbonentsQueryOptions } from '@/shared/api/orval/osi/osi';
import { DbAbonent } from '@/shared/api/orval/models';
import { AreaTypeCodes } from '@shared/types/dictionaries';

@injectable()
export class OsiPastInvoicesViewModelImpl implements OsiPastInvoicesViewModel {
  date: Date = new Date();

  isLoading = false;

  // OSI-845: галочка «Отдельными квитанциями» (по умолчанию снята, в localStorage не сохраняется)
  isSeparateFiles = false;

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);

    void this.loadAbonents();
  }

  private _abonents: DbAbonent[] = [];

  get abonents() {
    return this._abonents
      .map((abonent) => {
        // Фаза 2: не мутируем flat. Арендатор → flatDisplay (единый номер от backend),
        // остальные типы — сырой flat как раньше. Скачивание квитанций идёт по id (selectionModel).
        if (abonent.areaTypeCode === AreaTypeCodes.ARENDATOR) {
          return { ...abonent, flat: abonent.flatDisplay ?? abonent.flat };
        }
        return abonent;
      })
      .sort((a, b) => flatComparator(a?.flat, b?.flat));
  }

  get abonentsIsNotSelected() {
    if (!(this._selectionModel as GridRowId[]).length) return true;

    return false;
  }

  private _selectionModel: GridInputSelectionModel = [];

  get selectionModel() {
    return this._selectionModel;
  }

  onSelectionModelChange = (model: GridSelectionModel) => {
    this._selectionModel = model;
  };

  // OSI-845: переключение галочки «Отдельными квитанциями»
  setIsSeparateFiles = (value: boolean) => {
    this.isSeparateFiles = value;
  };

  downloadInvoices = async () => {
    try {
      if (!this._selectionModel) throw new Error('Не выбраны помещения!');

      // OSI-845: фиксируем значение галочки на момент запуска (пока идёт запрос, она заблокирована)
      const separateFiles = this.isSeparateFiles;

      const mutation = queryClient.getMutationCache().build(
        queryClient,
        getPostApiReportsAbonentsInvoicesPdfOnPeriodMutationOptions({
          // OSI-845: для отдельных файлов — увеличенный таймаут (сборка ZIP может длиться минуты);
          // без галочки опции запроса прежние.
          request: separateFiles
            ? { responseType: 'blob', timeout: SEPARATE_INVOICES_REQUEST_TIMEOUT }
            : { responseType: 'blob' },
          mutation: {
            onMutate: () => {
              this.isLoading = true;
            },
            onSettled: () => {
              this.isLoading = false;
            }
          }
        })
      );

      const rawInvoices = await mutation.execute({
        data: this._selectionModel as number[],
        params: {
          year: this.date.getFullYear(),
          month: this.date.getMonth() + 1,
          isStraightOrder: true,
          isOnlyResidents: false,
          // OSI-845: параметр передаём только при включённой галочке — без неё запрос прежний
          ...(separateFiles ? { separateFiles: true } : {})
        }
      });

      if (separateFiles) {
        // OSI-845: сервер вернёт ZIP (или PDF, если квитанция одна) — формат по сигнатуре ответа
        await downloadPdfOrZip(
          rawInvoices as unknown as Blob,
          `Квитанции за_${this.date.getFullYear()}-${this.date.getMonth() + 1}`
        );
      } else {
        fileDownload(rawInvoices, `Квитанции за_${this.date.getFullYear()}-${this.date.getMonth() + 1}.pdf`);
      }
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        const error = e as AxiosError;
        let errorObj = error?.response?.data;
        let message = '';
        if (
          error.request.responseType === 'blob' &&
          error?.response?.data instanceof Blob &&
          error?.response?.data.type &&
          error?.response?.data.type.toLowerCase().indexOf('json') !== -1
        ) {
          errorObj = JSON.parse(await error.response.data.text());
          message = errorObj?.message;
        }
        notifications.error(message);
      }
      console.error(e);
    }
  };

  private loadAbonents = async () => {
    try {
      const { osiId } = this.osiModule;
      if (!osiId) throw new Error('osiId is not found');
      const queryOptions = getGetApiOsiIdAbonentsQueryOptions(osiId, { onlyArendators: false });
      const abonents = await queryClient.fetchQuery(queryOptions);
      if (abonents.code !== 0 || !abonents.result) return;

      this._abonents = abonents.result;
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        notifications.error((e as AxiosError)?.response?.data?.message);
      }
      console.error(e);
    }
  };
}
